import { useState, useCallback, useMemo } from "react";
import { useWindowDimensions } from "react-native";
import { PICKER_CONSTANTS } from "../types";
import { getStartOfDay, getEndOfDay } from "../utils/time-date-utils";

interface UsePickerFieldOptions {
  /** Initial visibility state of the picker modal */
  initialVisible?: boolean;
  /** Custom minimum date (defaults to start of today for time pickers) */
  minimumDate?: Date;
  /** Custom maximum date (defaults to end of today for time pickers) */
  maximumDate?: Date;
}

interface UsePickerFieldReturn {
  /** Whether the picker modal is visible */
  isVisible: boolean;
  /** Show the picker modal */
  show: () => void;
  /** Hide the picker modal */
  hide: () => void;
  /** Toggle the picker modal visibility */
  toggle: () => void;
  /** Calculated padding bottom for iOS modal styling */
  paddingBottom: number;
  /** Minimum date constraint */
  minimumDate: Date;
  /** Maximum date constraint */
  maximumDate: Date;
  /** Screen width for responsive calculations */
  screenWidth: number;
}

/**
 * Hook that provides shared logic for date/time picker fields:
 * - Modal visibility state management
 * - Responsive padding calculation for iOS modals
 * - Min/max date constraints
 * - Theme access
 *
 * @example
 * ```tsx
 * const { isVisible, show, hide, paddingBottom, theme, minimumDate, maximumDate } = usePickerField({
 *   minimumDate: new Date(2000, 0, 1),
 *   maximumDate: new Date(2030, 0, 1),
 * });
 * ```
 */
export function usePickerField(options: UsePickerFieldOptions = {}): UsePickerFieldReturn {
  const { initialVisible = false, minimumDate: customMinDate, maximumDate: customMaxDate } = options;

  const [isVisible, setIsVisible] = useState(initialVisible);
  const { width: screenWidth } = useWindowDimensions();

  // Calculate responsive padding for iOS modal
  const paddingBottom = useMemo(
    () => Math.round(screenWidth * PICKER_CONSTANTS.MODAL_PADDING_MULTIPLIER - PICKER_CONSTANTS.MODAL_PADDING_OFFSET),
    [screenWidth],
  );

  // Default min/max dates for time pickers (full day range)
  const minimumDate = useMemo(() => customMinDate ?? getStartOfDay(), [customMinDate]);

  const maximumDate = useMemo(() => customMaxDate ?? getEndOfDay(), [customMaxDate]);

  const show = useCallback(() => setIsVisible(true), []);
  const hide = useCallback(() => setIsVisible(false), []);
  const toggle = useCallback(() => setIsVisible((v) => !v), []);

  return {
    isVisible,
    show,
    hide,
    toggle,
    paddingBottom,
    minimumDate,
    maximumDate,
    screenWidth,
  };
}
